import { Optional } from './promiseExtensions';

class FunctorAsync<T> {
	functor: Promise<Functor<T>>;

	constructor(functor: Promise<Functor<T>>) {
		this.functor = functor;
	}

	public map<B>(f: (t: T) => B): FunctorAsync<B> {
		const a = async () => {
			const t: Functor<T> = await this.functor;
			const r: B = f(t.get());
			const result: Functor<B> = Functor.From(r);
			return result;
		};
		return new FunctorAsync(a());
	}

	public mapAsync<B>(f: (t: T) => Promise<B>): FunctorAsync<B> {
		const a = async () => {
			const t: Functor<T> = await this.functor;
			const r: B = await f(t.get());
			const result: Functor<B> = Functor.From(r);
			return result;
		};
		return new FunctorAsync(a());
	}

	public async get(): Promise<T> {
		const functor = await this.functor;
		return functor.t;
	}

	public log(message?: string): FunctorAsync<T> {
		const a = async () => {
			const t: Functor<T> = await this.functor;
			const r = t.get();
			Optional.Maybe(message)
				.do((e) => console.info(e, r))
				.orElseDo(() => console.info(r));
			const result: Functor<T> = Functor.From(r);
			return result;
		};
		return new FunctorAsync(a());
	}

	public async toOptional(): Promise<Optional<T>> {
		try {
			const functor = await this.functor;
			return Optional.Just(functor.t);
		} catch (e) {
			return Optional.None();
		}
	}
}

export class Functor<T> {
	readonly t: T;

	private constructor(instance: T) {
		this.t = instance;
	}

	public static From = <T>(b: T): Functor<T> => {
		return new Functor(b);
	};

	public map<B>(f: (t: T) => B): Functor<B> {
		return Functor.From(f(this.t));
	}

	public mapAsync<B>(f: (t: T) => Promise<B>): FunctorAsync<B> {
		const input = async () => Functor.From(await f(this.t));
		return new FunctorAsync(input());
	}

	public log(message?: string): Functor<T> {
		Optional.Maybe(message)
			.do((e) => console.info(e, this.t))
			.orElseDo(() => console.info(this.t));
		return this;
	}

	public get(): T {
		return this.t;
	}
}
